import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
} from '@mui/material';
import type { IncidentReviewOutcome } from '../../services/incidentsApi';

const REVIEW_OUTCOME_TO_CLOSE_REASON: Record<IncidentReviewOutcome, string> = {
  true_event: 'TRUE_POSITIVE',
  false_positive: 'FALSE_POSITIVE',
  demo_or_test_activity: 'TEST',
  duplicate: 'DUPLICATE',
  expected_activity: 'NOT_ACTIONABLE',
  insufficient_evidence: 'NOT_ACTIONABLE',
  not_actionable: 'NOT_ACTIONABLE',
};

const CLOSE_REASON_TO_REVIEW_OUTCOME: Record<string, IncidentReviewOutcome> = Object.entries(
  REVIEW_OUTCOME_TO_CLOSE_REASON,
).reduce((acc, [reviewOutcome, closeReason]) => {
  if (!acc[closeReason]) {
    acc[closeReason] = reviewOutcome as IncidentReviewOutcome;
  }
  return acc;
}, {} as Record<string, IncidentReviewOutcome>);

const REVIEW_OUTCOME_LABELS: Record<string, string> = {
  true_event: 'True event',
  false_positive: 'False positive',
  demo_or_test_activity: 'Special-case activity',
  duplicate: 'Duplicate',
  expected_activity: 'Expected activity / not actionable',
  insufficient_evidence: 'Insufficient evidence',
};

function reviewOutcomeLabel(value: IncidentReviewOutcome | string | null | undefined): string {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  return REVIEW_OUTCOME_LABELS[normalized] || normalized.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export type IncidentResolveDialogPresentation = {
  title: string;
  submitLabel: string;
  info: string;
};

export interface IncidentResolveDialogProps {
  open: boolean;
  onClose: () => void;
  incidentId: string | undefined;
  initialReason?: string;
  initialReviewOutcome?: IncidentReviewOutcome | null;
  existingReviewOutcome?: IncidentReviewOutcome | string | null;
  existingReviewOutcomeSource?: string | null;
  blockedReviewOutcomes?: IncidentReviewOutcome[];
  reviewOutcomeBlockReason?: string | null;
  drawerBusy: string | null;
  canManage: boolean;
  presentation?: IncidentResolveDialogPresentation | null;
  onResolve: (params: {
    reason: string;
    reviewOutcome: IncidentReviewOutcome;
    note: string;
    tags: string[];
  }) => Promise<void>;
}

const DEFAULT_PRESENTATION: IncidentResolveDialogPresentation = {
  title: 'Close incident',
  submitLabel: 'Close incident',
  info:
    'Closing the incident updates the workflow immediately, records the closure reason, and does not depend on evidence being present.',
};

const IncidentResolveDialog: React.FC<IncidentResolveDialogProps> = ({
  open,
  onClose,
  incidentId,
  initialReason,
  initialReviewOutcome,
  existingReviewOutcome,
  existingReviewOutcomeSource,
  blockedReviewOutcomes = [],
  reviewOutcomeBlockReason,
  drawerBusy,
  canManage,
  presentation,
  onResolve,
}) => {
  const [reviewOutcome, setReviewOutcome] = useState<IncidentReviewOutcome>('true_event');
  const [note, setNote] = useState('');
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);

  useEffect(() => {
    if (open) {
      setReviewOutcome(initialReviewOutcome || CLOSE_REASON_TO_REVIEW_OUTCOME[initialReason || ''] || 'true_event');
      setNote('');
      setTags('');
      setSubmitError(null);
      setSubmitting(false);
      setOverwriteConfirmed(false);
    }
  }, [open, initialReason, initialReviewOutcome]);

  const copy = presentation || DEFAULT_PRESENTATION;
  const normalizedExistingReviewOutcome = String(existingReviewOutcome || '').trim().toLowerCase();
  const existingExplicitReview = normalizedExistingReviewOutcome
    && String(existingReviewOutcomeSource || '').trim().toLowerCase() === 'explicit';
  const reviewOutcomeBlocked = blockedReviewOutcomes.includes(reviewOutcome);
  const overwriteWarningActive = Boolean(
    existingExplicitReview
    && normalizedExistingReviewOutcome !== String(reviewOutcome || '').trim().toLowerCase(),
  );
  const submitDisabled = !incidentId
    || !!drawerBusy
    || !canManage
    || submitting
    || reviewOutcomeBlocked
    || (overwriteWarningActive && !overwriteConfirmed);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {copy.info}
        </Alert>
        {!incidentId ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No representative evidence is selected for this review decision. Re-open the recurring risk pattern from the queue and try again.
          </Alert>
        ) : null}
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}
        {reviewOutcomeBlocked && reviewOutcomeBlockReason ? (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="review-outcome-block-alert">
            {reviewOutcomeBlockReason}
          </Alert>
        ) : null}
        {overwriteWarningActive ? (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="review-outcome-overwrite-alert">
            This observation already has a recorded review outcome of <strong>{reviewOutcomeLabel(existingReviewOutcome)}</strong>.
            Saving now will replace it with <strong>{reviewOutcomeLabel(reviewOutcome)}</strong>.
          </Alert>
        ) : null}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Closure reason</InputLabel>
          <Select
            label="Closure reason"
            value={reviewOutcome}
            onChange={(e) => {
              setReviewOutcome(e.target.value as IncidentReviewOutcome);
              setOverwriteConfirmed(false);
            }}
          >
            <MenuItem value="true_event" disabled={blockedReviewOutcomes.includes('true_event')}>True event</MenuItem>
            <MenuItem value="false_positive" disabled={blockedReviewOutcomes.includes('false_positive')}>False positive</MenuItem>
            <MenuItem value="demo_or_test_activity" disabled={blockedReviewOutcomes.includes('demo_or_test_activity')}>Special-case activity</MenuItem>
            <MenuItem value="duplicate" disabled={blockedReviewOutcomes.includes('duplicate')}>Duplicate</MenuItem>
            <MenuItem value="expected_activity" disabled={blockedReviewOutcomes.includes('expected_activity')}>Expected activity / not actionable</MenuItem>
            <MenuItem value="insufficient_evidence" disabled={blockedReviewOutcomes.includes('insufficient_evidence')}>Insufficient evidence</MenuItem>
          </Select>
        </FormControl>
        {overwriteWarningActive ? (
          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={overwriteConfirmed}
                onChange={(e) => setOverwriteConfirmed(e.target.checked)}
              />
            }
            label="I understand this will replace the saved review outcome for this observation."
          />
        ) : null}
        <TextField
          fullWidth
          label="Corrective action note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Tags (optional, comma-separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          helperText="Example: forklift, pedestrian, PPE, spill"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={submitDisabled}
          onClick={() => {
            const parsedTags = tags.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 20);
            void (async () => {
              setSubmitError(null);
              setSubmitting(true);
              try {
                await onResolve({
                  reason: REVIEW_OUTCOME_TO_CLOSE_REASON[reviewOutcome] || 'TRUE_POSITIVE',
                  reviewOutcome,
                  note,
                  tags: parsedTags,
                });
                onClose();
              } catch (error: unknown) {
                const message = error instanceof Error ? error.message : 'Failed to save review decision';
                setSubmitError(message);
              } finally {
                setSubmitting(false);
              }
            })();
          }}
        >
          {submitting ? 'Saving…' : copy.submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentResolveDialog;
